import type {
  FormEvent,
  KeyboardEventHandler,
  MouseEvent,
  MouseEventHandler,
} from "react";
import { useState } from "react";
import emotionStyled from "@emotion/styled";
import { IconButtonV2 } from "src/components/IconButtonV2/IconButtonV2.tsx";
import { css, keyframes } from "@emotion/react";
import { BaseInputV2 } from "src/components/BaseInputV2/BaseInputV2.tsx";
import { useControlled } from "@exonn/hooks";
import Minus from "./minus.svg?react";

const minusStyle = css`
  &:hover path {
    fill: white;
  }
`;

export interface CounterProps {
  onChange?: (value: number) => void;
  count?: number;
  defaultCount?: number;
  min?: number;
  isDeleting?: boolean;
  max?: number;
  isIncrementing?: boolean;
  isDecrementing?: boolean;
  disableDeleting?: boolean;
  disableIncrementing?: boolean;
  disableDecrementing?: boolean;
  disableInput?: boolean;
  onDelete?: () => void;
}

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(value, min));
};

export const Counter = (props: CounterProps) => {
  const {
    onChange,
    count: countProp,
    defaultCount = 1,
    min = 1,
    max = Infinity,
    isDeleting = false,
    isDecrementing = false,
    isIncrementing = false,
    disableIncrementing = false,
    disableDeleting = false,
    disableDecrementing = false,
    disableInput = false,
    onDelete,
  } = props;

  const [count, setCount] = useControlled({
    name: "Counter",
    state: "count",
    controlled: countProp,
    default: clamp(defaultCount, min, max),
    onChange: onChange,
  });

  const handleKeyUp: KeyboardEventHandler<HTMLInputElement> = (e) => {
    // we are handling only Enter key
    if (e.key != "Enter") return;

    e.currentTarget.blur();
  };

  const handleIncrement: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    const nextCount = clamp(count + 1, min, max);
    setCount(nextCount);
  };
  const handleDecrement: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    const nextCount = clamp(count - 1, min, max);
    setCount(nextCount);
  };
  const [editMode, setEditMode] = useState(false);
  const [draftCount, setDraftCount] = useState(count);

  const handleChange = (e: FormEvent<HTMLInputElement>) => {
    const numberOrEmptyRegExp = /^\d*$/;
    const numberOrString = e.currentTarget?.value?.trim();

    // allow only integers or empty string to be entered
    if (!numberOrEmptyRegExp.test(numberOrString)) return;

    setDraftCount(Number(numberOrString) ?? count);
  };

  const handleFocus = () => {
    setEditMode(true);
    setDraftCount(count);
  };

  const handleInputClick = (e: MouseEvent) => {
    // Do not trigger parent components click event handlers
    e.stopPropagation();
  };

  const handleBlur = () => {
    setEditMode(false);
    if (count === draftCount) {
      return;
    }
    if (draftCount < min) {
      onDelete?.();
    } else {
      setCount(clamp(draftCount, min, max));
    }
  };

  const handleDelete: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        width: "130px",
      }}
    >
      {count <= min && (
        <IconButtonV2
          onClick={handleDelete}
          skin="danger"
          variant="outlined"
          size="sm"
          disabled={isDeleting || disableDeleting}
          style={{ padding: "5px 10px" }}
        >
          {isDeleting ? (
            <Spinner />
          ) : (
            <i style={{ display: "flex" }} className={"icon-15-trash"} />
          )}
        </IconButtonV2>
      )}
      {count > min && (
        <IconButtonV2
          onClick={handleDecrement}
          skin="info"
          variant="outlined"
          size="sm"
          disabled={isDecrementing || disableDecrementing}
          style={{ padding: "5px 10px" }}
          css={minusStyle}
        >
          {isDecrementing ? <Spinner /> : <Minus />}
        </IconButtonV2>
      )}
      <BaseInputV2
        onKeyUp={handleKeyUp}
        disabled={disableInput}
        css={{
          width: 50,
          textAlign: "center",
        }}
        onClick={handleInputClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={String(editMode ? draftCount : count)}
        onChange={handleChange}
      />

      <IconButtonV2
        onClick={handleIncrement}
        skin="info"
        size="sm"
        disabled={isIncrementing || disableIncrementing || count >= max}
        style={{ padding: "5px 10px" }}
      >
        {isIncrementing ? <Spinner /> : <i className={"icon-15-add"} />}
      </IconButtonV2>
    </div>
  );
};

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = emotionStyled.i`
  animation: ${spin} 1s linear infinite;
`;

Spinner.defaultProps = {
  className: "fi fi-rs-spinner",
};
